import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import { WhatsAppCTA } from "../components/Buttons";
import { PhoneIconBtn, MailIconBtn } from "../components/ContactIcons";
import { SITE } from "../lib/site";
import useSEO from "../hooks/useSEO";

const REP_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Tax Representation and Dispute Resolution",
  "provider": { "@id": "https://www.svmd.in/#org" },
  "areaServed": { "@type": "Country", "name": "India" },
  "name": "Representation & Dispute Resolution",
  "description":
    "End-to-end handling of income tax notices (142(1), 143(2), 148), GST DRC-01 / ASMT-10 proceedings, faceless assessments, scrutiny, penalty proceedings, MSME disputes, RERA representation, and appellate support.",
  "url": "https://www.svmd.in/representation-and-dispute",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Representation matters handled",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Income Tax Notices & Assessments — 142(1), 143(2), 148" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Faceless Assessments and Scrutiny" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "GST DRC-01 / DRC-01A / ASMT-10 proceedings" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Penalty Proceedings & Appellate Support" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "MSME Dispute Resolution" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "RERA Representation & Disputes" } }
    ]
  }
};

const REACH_OUT = [
  "A notice has been received",
  "A response deadline is approaching",
  "An assessment is underway",
  "A payment is stuck",
  "A dispute has escalated",
];

const HANDLE = [
  {
    title: "Income Tax",
    items: ["142(1), 143(2), 148 notices", "Scrutiny and reassessment", "Penalty proceedings", "Faceless assessments"],
  },
  {
    title: "GST",
    items: ["DRC-01 / DRC-01A", "ASMT-10 notices", "ITC disputes", "Registration issues"],
  },
  {
    title: "MSME Disputes",
    items: ["Delayed payments", "MSME Samadhaan filings", "Interest claims", "Recovery support"],
    cta: { to: "/msme-recovery", label: "MSME Payment Recovery" },
  },
  {
    title: "RERA",
    items: ["Authority queries and notices", "Project compliance issues", "Documentation and certification support", "Coordination with legal counsel"],
  },
  {
    title: "Representation",
    items: ["Drafting responses", "Filing and submissions", "Appearance before authorities", "Appellate support"],
  },
];

const STEPS = [
  { title: "Review", body: "Understand the notice and context." },
  { title: "Position", body: "Define response strategy." },
  { title: "Prepare", body: "Documentation and drafting." },
  { title: "Follow Through", body: "Submission to closure." },
];

const WHY = [
  "Weak responses escalate issues",
  "Poor documentation affects outcomes",
  "Delays make resolution harder",
];

export default function Representation() {
  useSEO({
    title:
      "Income Tax & GST Notice Representation in India | SVMD & Associates",
    description:
      "We handle income tax notices (142(1), 143(2), 148), GST DRC-01 / ASMT-10 proceedings, faceless assessments, penalty proceedings, MSME disputes, and RERA matters end-to-end — from first response to closure.",
    keywords:
      "income tax notice reply, 142(1) notice, 143(2) notice, 148 notice, faceless assessment, scrutiny assessment, GST notice reply, DRC-01, DRC-01A, ASMT-10, GST appeal, penalty proceedings, RERA representation, tax dispute resolution India, CA representation Coimbatore",
    canonical: "/representation-and-dispute",
    jsonLd: REP_JSONLD,
  });
  return (
    <>
      {/* HERO (DARK with strong glow) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-100 animate-grid-drift" aria-hidden />
        <div className="absolute inset-0 bg-glow-radial" aria-hidden />
        <div className="absolute -right-40 -top-40 w-[800px] h-[800px] rounded-full blur-3xl opacity-50 bg-[radial-gradient(circle,rgba(212,255,63,0.18)_0%,transparent_60%)]" aria-hidden />
        <div className="absolute -left-40 bottom-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-40 bg-[radial-gradient(circle,rgba(37,99,235,0.5)_0%,transparent_60%)]" aria-hidden />

        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-20 md:pb-28">
          <Reveal>
            <div className="flex items-center gap-3 text-[12px] text-paper/55 mb-6">
              <Link to="/services" className="hover:text-voltage" data-testid="bc-services">Services</Link>
              <span className="text-paper/30">/</span>
              <span className="text-voltage">Representation &amp; Dispute</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-semibold text-[40px] sm:text-[56px] md:text-[72px] lg:text-[84px] leading-[1.04] tracking-[-0.03em] max-w-5xl">
              Notices. Disputes. Representation&mdash;<br className="hidden md:block" />
              <span className="text-voltage">handled end-to-end.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-2xl text-paper/75 text-base md:text-[18px] leading-[1.55]">
              Income tax, GST, MSME, and RERA matters reviewed, responded to,
              and resolved with a structured approach.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <WhatsAppCTA label="Share your notice" testId="rep-hero-whatsapp-cta" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHEN CLIENTS REACH OUT (LIGHT) */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">When clients reach out</div>
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                The first response matters.
              </h2>
            </Reveal>
            <Reveal delay={120} className="col-span-12 md:col-span-7 md:pl-8 md:border-l border-[var(--border-light)] flex items-end">
              <p className="text-[15px] md:text-[16px] text-slate-muted leading-relaxed">
                Most clients reach out at the points listed here. At this
                stage, the first response defines the trajectory of the matter.
              </p>
            </Reveal>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {REACH_OUT.map((r, i) => (
              <Reveal key={r} delay={i * 80}>
                <li
                  data-testid={`rep-reach-${i}`}
                  className="bg-surface-bright rounded-xl p-6 border border-[var(--border-light)] flex items-start gap-4 h-full hover:-translate-y-1 transition-transform"
                >
                  <AlertCircle size={20} strokeWidth={1.6} className="text-voltage shrink-0 mt-0.5 fill-voltage/10" />
                  <span className="font-display text-[17px] leading-snug">{r}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT WE HANDLE (DARK, glass cards) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-70" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <Reveal>
              <div className="eyebrow eyebrow-voltage mb-4">What we handle</div>
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                Across forums and authorities.
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HANDLE.map((g, i) => (
              <Reveal key={g.title} delay={i * 90}>
                <div
                  data-testid={`rep-handle-${g.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative glass-panel rounded-xl p-6 h-full flex flex-col ${
                    g.cta ? "ring-1 ring-voltage/40" : ""
                  }`}
                >
                  {g.cta && <span className="absolute top-0 left-7 right-7 h-px bg-voltage" />}
                  <div className="font-mono text-[10px] tracking-[0.18em] text-paper/55 mb-3">
                    {String(i + 1).padStart(2, "0")} / 05
                  </div>
                  <h3 className="font-display text-[20px] md:text-[22px]">{g.title}</h3>
                  <ul className="mt-4 space-y-2 flex-1">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-baseline gap-3 text-paper/80 text-[14px]">
                        <span className="w-1 h-1 rounded-full bg-voltage shrink-0 translate-y-1" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  {g.cta && (
                    <Link
                      to={g.cta.to}
                      data-testid={`rep-cta-${g.cta.to}`}
                      className="mt-6"
                    >
                      <span className="voltage-cta inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-[13px] font-medium">
                        {g.cta.label}
                        <ArrowRight size={14} strokeWidth={2} />
                      </span>
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE HANDLE THIS — 4 BIG NUMBERED STEPS (LIGHT) */}
      <section className="bg-surface text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow mb-4">How we handle this</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em] max-w-3xl">
              A defined four-step process.
            </h2>
          </Reveal>

          <div className="mt-12 relative">
            {/* Connecting line on desktop */}
            <div className="hidden md:block absolute left-0 right-0 top-10 h-px bg-[linear-gradient(90deg,transparent_0%,#D4FF3F_20%,#D4FF3F_80%,transparent_100%)] opacity-50" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-5 relative">
              {STEPS.map((s, i) => (
                <Reveal key={s.title} delay={i * 120}>
                  <div data-testid={`rep-step-${i + 1}`} className="relative">
                    <div className="font-display font-semibold text-[64px] md:text-[80px] leading-none tracking-[-0.04em] text-ink/90">
                      <span style={{ WebkitTextStroke: "1px #2563EB", color: "transparent" }}>{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="mt-3 font-display text-[20px] md:text-[22px]">{s.title}</h3>
                    <p className="mt-2 text-slate-muted text-[14px] leading-relaxed max-w-[18ch]">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS (DARK) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(37,99,235,0.18)_0%,transparent_55%)]" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-4">Why this matters</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em] max-w-3xl">
              Handled correctly at the first stage, escalation is avoided.
            </h2>
          </Reveal>

          <ul className="mt-10 space-y-5 md:space-y-6 max-w-4xl">
            {WHY.map((w, i) => (
              <Reveal key={w} delay={i * 120}>
                <li className="flex items-start gap-6 md:gap-9">
                  <span className="font-mono text-[10px] tracking-[0.18em] text-voltage shrink-0 mt-2 md:mt-3">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display font-medium text-[20px] md:text-[26px] leading-[1.3] tracking-tight">
                    {w}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>

          <Reveal delay={400}>
            <p className="mt-9 text-paper/70 text-[15px] md:text-[16px] max-w-2xl">
              Handling this correctly at the first stage avoids these risks.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CLOSE (DARK gradient) */}
      <section className="relative bg-hero-gradient text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <h2 className="font-display font-semibold text-[32px] md:text-[52px] leading-[1.06] tracking-[-0.025em] max-w-4xl">
              If you&rsquo;ve received a notice or are dealing with a dispute, share it with us.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <WhatsAppCTA label="Share your notice" testId="rep-close-whatsapp-cta" />
              <PhoneIconBtn variant="dark" testId="rep-close-phone-icon" />
              <MailIconBtn variant="dark" testId="rep-close-email-icon" />
            </div>
          </Reveal>
          <Reveal delay={250}>
            <div className="mt-7">
              <Link to="/msme-recovery" data-testid="rep-bottom-msme-link" className="link-arrow text-paper/85 hover:text-voltage text-[14px]">
                <span className="link-wipe">MSME Payment Recovery</span>
                <ArrowRight size={14} strokeWidth={2} className="arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
